import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Card, CardActionArea, CardContent, Chip, CircularProgress, Grid, Radio, Skeleton, TextField, Typography } from '@mui/material';
import { AccountBalanceWallet as WalletIcon, CreditCard as CreditCardIcon, PhoneIphone as PhoneIcon } from '@mui/icons-material';
import { CardCvcElement, CardExpiryElement, CardNumberElement, Elements, PaymentRequestButtonElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, type PaymentRequest } from '@stripe/stripe-js';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ParentPortalLayout from '../components/ParentPortalLayout';
import { parentPortalAPI } from '../utils/api';
import { useParentAuth } from '../hooks/useParentAuth';
import type { SubscriptionPlan, SubscriptionStatus } from '../types/parent';
import { parentPortalPrimaryButtonSx, parentPortalSolidButtonSx, parentPortalTheme } from '../theme/parentPortalTheme';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '');
const elementStyle = { base: { fontSize: '16px', color: parentPortalTheme.stripe.text, '::placeholder': { color: parentPortalTheme.stripe.placeholder } } };

const formatMoney = (plan: SubscriptionPlan) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: plan.currency || 'USD' }).format((plan.amount || 0) / 100);

const StripeField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ mb: 2 }}>
    <Typography sx={{ fontWeight: 800, mb: 0.75 }}>{label}</Typography>
    <Box sx={{ bgcolor: '#fff', border: '1.5px solid #d1d5db', borderRadius: 3, px: 2, py: 1.6 }}>{children}</Box>
  </Box>
);

const ParentSubscribePreloader: React.FC = () => (
  <Box>
    <Grid container spacing={2} sx={{ mb: 4 }}>
      {[0, 1].map((index) => (
        <Grid key={index} size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 5, bgcolor: index === 0 ? parentPortalTheme.subscribe.planSelected : '#fff', border: `2px solid ${parentPortalTheme.subscribe.planBorder}` }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Skeleton variant="text" width="42%" height={30} sx={{ borderRadius: 2 }} />
                <Skeleton variant="circular" width={28} height={28} />
              </Box>
              <Skeleton variant="rounded" width={110} height={28} sx={{ borderRadius: 999, mb: 2, bgcolor: parentPortalTheme.subscribe.bestValueChip }} />
              <Skeleton variant="text" width="58%" height={48} sx={{ borderRadius: 2, mb: 1 }} />
              {[0, 1, 2].map((line) => (
                <Box key={line} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <Skeleton variant="circular" width={8} height={8} />
                  <Skeleton variant="text" width={`${62 + line * 8}%`} height={22} sx={{ borderRadius: 2 }} />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
    <Skeleton variant="text" width={180} height={32} sx={{ borderRadius: 2, mb: 2 }} />
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {[0, 1, 2].map((index) => (
        <Grid key={index} size={{ xs: 12, sm: 4 }}>
          <Skeleton variant="rounded" height={42} sx={{ borderRadius: 4, bgcolor: index === 2 ? 'rgba(38, 155, 178, 0.18)' : 'rgba(255,255,255,0.85)' }} />
        </Grid>
      ))}
    </Grid>
    <Box sx={{ bgcolor: parentPortalTheme.subscribe.panelBg, borderRadius: 5, p: 3 }}>
      <Skeleton variant="rounded" height={56} sx={{ borderRadius: 3, mb: 2 }} />
      <Skeleton variant="rounded" height={56} sx={{ borderRadius: 3, mb: 2 }} />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}><Skeleton variant="rounded" height={56} sx={{ borderRadius: 3 }} /></Grid>
        <Grid size={{ xs: 12, sm: 6 }}><Skeleton variant="rounded" height={56} sx={{ borderRadius: 3 }} /></Grid>
      </Grid>
      <Skeleton variant="rounded" width={190} height={46} sx={{ borderRadius: 999, mt: 3, bgcolor: 'rgba(73, 185, 217, 0.25)' }} />
    </Box>
  </Box>
);

const CheckoutForm: React.FC<{ plans: SubscriptionPlan[]; selectedPlan: SubscriptionPlan | null; onPlanSelect: (planId: string) => void }> = ({ plans, selectedPlan, onPlanSelect }) => {
  const { t } = useTranslation('pages', { keyPrefix: 'parentSubscribe' });
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { refreshProfile } = useParentAuth();
  const [method, setMethod] = useState<'card' | 'wallet'>('card');
  const [cardholderName, setCardholderName] = useState('');
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);
  const [walletAvailability, setWalletAvailability] = useState<{ applePay?: boolean; googlePay?: boolean }>({});
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!stripe || !selectedPlan) return;
    const request = stripe.paymentRequest({
      country: 'US',
      currency: selectedPlan.currency.toLowerCase(),
      total: { label: selectedPlan.name, amount: selectedPlan.amount },
      requestPayerName: true,
      requestPayerEmail: true,
    });
    request.canMakePayment().then(result => {
      if (result) {
        setPaymentRequest(request);
        setWalletAvailability({ applePay: Boolean(result.applePay), googlePay: Boolean(result.googlePay) });
      } else {
        setPaymentRequest(null);
        setWalletAvailability({});
        setMethod('card');
      }
    });
  }, [selectedPlan, stripe]);

  useEffect(() => {
    if (!paymentRequest || !selectedPlan) return undefined;
    const handler = async (event: any) => {
      let completed = false;
      try {
        const setupResponse = await parentPortalAPI.createSetupIntent();
        const clientSecret = setupResponse.client_secret || setupResponse.data?.client_secret;
        if (!clientSecret || !stripe) throw new Error(t('missingClientSecret'));
        const result = await stripe.confirmCardSetup(clientSecret, { payment_method: event.paymentMethod.id }, { handleActions: false });
        if (result.error) throw new Error(result.error.message);
        event.complete('success');
        completed = true;
        if (result.setupIntent?.status === 'requires_action') {
          const actionResult = await stripe.confirmCardSetup(clientSecret);
          if (actionResult.error) throw new Error(actionResult.error.message);
        }
        await parentPortalAPI.subscribe(selectedPlan.plan_id, event.paymentMethod.id);
        await refreshProfile();
        navigate('/parent/dashboard', { replace: true });
      } catch (err: any) {
        if (!completed) event.complete('fail');
        setError(err?.response?.data?.message || err?.message || t('subscribeFailed'));
      }
    };
    paymentRequest.on('paymentmethod', handler);
    return () => {
      paymentRequest.off('paymentmethod', handler);
    };
  }, [navigate, paymentRequest, refreshProfile, selectedPlan, stripe, t]);

  const submit = async () => {
    if (!stripe || !elements || !selectedPlan) return;
    setSubmitting(true);
    setError(null);
    setMessage(null);
    try {
      const setupResponse = await parentPortalAPI.createSetupIntent();
      const clientSecret = setupResponse.client_secret || setupResponse.data?.client_secret;
      const card = elements.getElement(CardNumberElement);
      if (!clientSecret || !card) throw new Error(t('missingClientSecret'));
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card, billing_details: { name: cardholderName } },
      });
      if (result.error || !result.setupIntent?.payment_method) throw new Error(result.error?.message || t('subscribeFailed'));
      await parentPortalAPI.subscribe(selectedPlan.plan_id, String(result.setupIntent.payment_method));
      setMessage(t('success'));
      await refreshProfile();
      navigate('/parent/dashboard', { replace: true });
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || t('subscribeFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {plans.map(plan => (
          <Grid key={plan.plan_id} size={{ xs: 12, md: 6 }}>
            <Card sx={{ borderRadius: 5, bgcolor: selectedPlan?.plan_id === plan.plan_id ? parentPortalTheme.subscribe.planSelected : '#fff', border: `2px solid ${parentPortalTheme.subscribe.planBorder}` }}>
              <CardActionArea onClick={() => onPlanSelect(plan.plan_id)}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontWeight: 900, textTransform: 'uppercase' }}>{plan.name}</Typography>
                    <Radio checked={selectedPlan?.plan_id === plan.plan_id} />
                  </Box>
                  {plan.interval === 'year' && <Chip label={t('bestValue')} sx={{ bgcolor: parentPortalTheme.subscribe.bestValueChip, fontWeight: 900, mb: 1 }} />}
                  <Typography variant="h4" sx={{ fontWeight: 900 }}>{formatMoney(plan)} <Typography component="span">/ {t(`intervals.${plan.interval}`)}</Typography></Typography>
                  <Box component="ul" sx={{ pl: 2.5, mt: 2 }}>
                    {plan.features.map(feature => <li key={feature}><Typography>{feature}</Typography></li>)}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>{t('paymentMethod')}</Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {paymentRequest && (
          <>
            {walletAvailability.googlePay && <Grid size={{ xs: 12, sm: 4 }}><Button fullWidth onClick={() => setMethod('wallet')} startIcon={<WalletIcon />} sx={{ ...(method === 'wallet' ? parentPortalSolidButtonSx : { bgcolor: '#fff', color: parentPortalTheme.text.primary }), borderRadius: 4 }}>{t('googlePay')}</Button></Grid>}
            {walletAvailability.applePay && <Grid size={{ xs: 12, sm: 4 }}><Button fullWidth onClick={() => setMethod('wallet')} startIcon={<PhoneIcon />} sx={{ ...(method === 'wallet' ? parentPortalSolidButtonSx : { bgcolor: '#fff', color: parentPortalTheme.text.primary }), borderRadius: 4 }}>{t('applePay')}</Button></Grid>}
          </>
        )}
        <Grid size={{ xs: 12, sm: 4 }}><Button fullWidth onClick={() => setMethod('card')} startIcon={<CreditCardIcon />} sx={{ ...(method === 'card' ? parentPortalSolidButtonSx : { bgcolor: '#fff', color: parentPortalTheme.text.primary }), borderRadius: 4 }}>{t('creditCard')}</Button></Grid>
      </Grid>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {method === 'wallet' && paymentRequest ? (
        <PaymentRequestButtonElement options={{ paymentRequest }} />
      ) : (
        <Box sx={{ bgcolor: parentPortalTheme.subscribe.panelBg, borderRadius: 5, p: 3 }}>
          <StripeField label={t('cardNumber')}><CardNumberElement options={{ style: elementStyle }} /></StripeField>
          <TextField fullWidth label={t('cardholderName')} value={cardholderName} onChange={(event) => setCardholderName(event.target.value)} sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}><StripeField label={t('expiry')}><CardExpiryElement options={{ style: elementStyle }} /></StripeField></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><StripeField label={t('cvv')}><CardCvcElement options={{ style: elementStyle }} /></StripeField></Grid>
          </Grid>
          <Button disabled={submitting || !selectedPlan} onClick={submit} sx={{ borderRadius: 999, px: 4, py: 1.3, fontWeight: 900, color: '#fff', ...parentPortalPrimaryButtonSx }}>
            {submitting ? <CircularProgress size={20} color="inherit" /> : t('subscribeButton', { price: selectedPlan ? formatMoney(selectedPlan) : '' , period: selectedPlan ? t(`periodShort.${selectedPlan.interval}`) : '' })}
          </Button>
        </Box>
      )}
    </>
  );
};

const ParentSubscribe: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'parentSubscribe' });
  const { refreshProfile } = useParentAuth();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectedPlan = useMemo(() => plans.find(plan => plan.plan_id === selectedPlanId) || null, [plans, selectedPlanId]);
  const activeSubscription = subscription?.status === 'active';

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const subscriptionResponse = await parentPortalAPI.getSubscription();
        const currentSubscription = subscriptionResponse.subscription || subscriptionResponse.data || null;
        setSubscription(currentSubscription);
        if (currentSubscription?.status !== 'active') {
          const plansResponse = await parentPortalAPI.getPlans();
          const list = plansResponse.plans || plansResponse.data || [];
          setPlans(list);
          setSelectedPlanId((list.find(plan => plan.interval === 'year') || list[0])?.plan_id || '');
        }
      } catch (err: any) {
        setError(err?.response?.data?.message || err?.message || t('loadPlansFailed'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [t]);

  const cancelSubscription = async () => {
    if (!window.confirm(t('cancelConfirm'))) return;
    setCancelling(true);
    setError(null);
    try {
      await parentPortalAPI.cancelSubscription();
      await refreshProfile();
      const response = await parentPortalAPI.getSubscription();
      const updatedSubscription = response.subscription || response.data || null;
      setSubscription(updatedSubscription);
      if (updatedSubscription?.status !== 'active') {
        const plansResponse = await parentPortalAPI.getPlans();
        const list = plansResponse.plans || plansResponse.data || [];
        setPlans(list);
        setSelectedPlanId((list.find(plan => plan.interval === 'year') || list[0])?.plan_id || '');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || t('cancelFailed'));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <ParentPortalLayout title={t('title')}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? <ParentSubscribePreloader /> : activeSubscription ? (
        <Box sx={{ bgcolor: parentPortalTheme.subscribe.activePanel, borderRadius: 5, p: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 900, mb: 1 }}>{t('manageTitle')}</Typography>
          <Typography sx={{ fontWeight: 800 }}>{subscription?.plan_name || t('activePlan')}</Typography>
          {subscription?.current_period_end && (
            <Typography sx={{ mt: 1 }}>{t('renews', { date: new Date(subscription.current_period_end).toLocaleDateString() })}</Typography>
          )}
          <Button disabled={cancelling} onClick={cancelSubscription} sx={{ mt: 3, borderRadius: 999, color: parentPortalTheme.button.cancel, fontWeight: 900 }}>
            {cancelling ? <CircularProgress size={18} /> : t('cancel')}
          </Button>
        </Box>
      ) : (
        <Elements stripe={stripePromise}>
          <CheckoutForm plans={plans} selectedPlan={selectedPlan} onPlanSelect={setSelectedPlanId} />
        </Elements>
      )}
    </ParentPortalLayout>
  );
};

export default ParentSubscribe;
